import { Button, Heading, Icon, Checkbox, IconButton, Input, InputGroup, InputLeftElement, Modal, ModalBody, ModalContent, ModalFooter, ModalHeader, ModalOverlay, Select, SimpleGrid, Stack, StackDivider, Table, TableContainer, Tbody, Td, Text, Textarea, Th, Thead, Tr, useDisclosure, useToast } from "@chakra-ui/react";
import { useEffect, useMemo, useState, useRef } from "react";
import { useSelector } from "react-redux";
import { DELETE, GET, GETFile, POST } from "../../utils/ApiRequestProvider";
import { FaArrowLeft, FaArrowRight, FaSearch, FaTrash, FaUndo } from "react-icons/fa";
import { RiDeleteBin5Fill } from "react-icons/ri";
import DocViewer, { DocViewerRef, DocViewerRenderers } from "@cyntler/react-doc-viewer";

export interface PaginationType {
    currentPage: number;
    totalPages: number;
    totalRecords: number;
}

interface FileViewerType {
    documentId: number;
    uri: string;
    fileName: string;
}

export default function RecycleBin() {
    const toast = useToast();
    
    const business = useSelector((state: any) => state.selections.value?.business);
        const [, setPagination] = useState<PaginationType>({ currentPage: 0, totalPages: 0, totalRecords: 0 });
    const [documents, setDocuments] = useState<Array<any>>([]);
    const [loading, setLoading] = useState(false);
    const [selectedDocumentPDF, setSelectedDocumentPDF] = useState<FileViewerType[]>([]);
    const [selectedDocument, setSelectedDocument] = useState<any>({});
    const [selectedDocuments, setSelectedDocuments] = useState<Array<any>>([]);
    const [search, setSearch] = useState('');
    const [docTypeFilter, setDocTypeFilter] = useState('');
    const [deleteReason, setDeleteReason] = useState('');
    const { isOpen: isDeleteOpen, onOpen: onDeleteOpen, onClose: onDeleteClose } = useDisclosure();
    const docViewerRef = useRef<DocViewerRef>(null);

    const docTypes = useMemo(() => {
        const set = new Set<string>();
        documents.forEach((d) => d.documentType && set.add(d.documentType));
        return Array.from(set);
    }, [documents]);

    const filteredDocs = useMemo(() => {
        const term = search.trim().toLowerCase();
        return documents.filter((d) => {
            if (docTypeFilter && d.documentType !== docTypeFilter) return false;
            if (!term) return true;
            return (
                String(d.id).includes(term) ||
                (d.documentType ?? '').toLowerCase().includes(term)
            );
        });
    }, [documents, search, docTypeFilter]);

    const confirmDelete = async () => {
        if (selectedDocuments.length === 0) {
            toast({ title: 'No document selected', status: 'warning', duration: 1200, position: 'top-right' });
            return;
        }
        try {
            await DELETE(`/api/Documents/${selectedDocuments.join(',')}/purge`, { reason: deleteReason });
            toast({ title: 'Deleted', status: 'success', duration: 1200, position: 'top-right' });
            setSelectedDocuments([]);
            setSelectedDocument({});
            setDeleteReason('');
            onDeleteClose();
            FetchList();
        } catch {
            toast({ title: 'Delete failed', status: 'error', duration: 1500, position: 'top-right' });
        }
    };

    const handleRestore = async () => {
        if (selectedDocuments.length === 0) {
            toast({ title: 'No document selected', status: 'warning', duration: 1200, position: 'top-right' });
            return;
        }
        try {
            const res = await POST('/api/Documents/restore', { documentIds: selectedDocuments });
            if (res.status === 'success') {
                toast({ title: 'Restored', status: 'success', duration: 1200, position: 'top-right' });
                setSelectedDocuments([]);
                setSelectedDocument({});
                FetchList();
            } else {
                toast({ title: res.message || 'Restore failed', status: 'error', duration: 1500, position: 'top-right' });
            }
        } catch {
            toast({ title: 'Restore failed', status: 'error', duration: 1500, position: 'top-right' });
        }
    };

    async function FetchList() {
        setLoading(true);
        try {
            if (business) {
                const res = await GET(`/api/Documents/${business?.id}/DeletedDocs?sortColumn=Date&sortOrder=desc`);
                setPagination(res.data.pagination);
                setDocuments(res.data.documents);
                console.log(res.data.documents);
            }
        } catch (err) {
            toast({
                title: 'Something Went Wrong!',
                description: 'An unexpected error occured while proceeding your request!',
                status: 'error',
                isClosable: false,
                duration: 1000,
                position: 'top-right'
            });
        }
        setLoading(false);
    }

    
    const handleSelectedDocument = async (e: any) => {
        if (e.target.checked) {
            // console.log(e.target.id);
            const id = parseInt(e.target.id);
            setSelectedDocuments([...selectedDocuments, e.target.id]);
            // console.log(documents.filter(doc => doc.id === id));
            setSelectedDocument(documents.filter(doc => doc.id === id));
        } else {
            setSelectedDocuments(selectedDocuments.filter((doc) => doc !== e.target.id));
            setSelectedDocument({});
        }
    }

    const getFilePDF = async () => {
        try {
            if(selectedDocument[0]?.file === undefined || selectedDocument[0]?.file === null){
                setSelectedDocumentPDF([]);
                return;
            }
            if(selectedDocumentPDF.find(doc => doc.documentId === selectedDocument[0]?.id) !== undefined){
                return;
            }
            const file = await GETFile(selectedDocument[0]?.file);
            setSelectedDocumentPDF([...selectedDocumentPDF, { documentId: selectedDocument[0]?.id, uri: URL.createObjectURL(file), fileName: file.name }]);
        } catch (err) {
            toast({
                title: 'Something Went Wrong!',
                description: 'An unexpected error occured while proceeding your request!',
                status: 'error',
                isClosable: false,
                duration: 1000,
                position: 'top-right'
            });
        }
    }

    useEffect(() => {
        getFilePDF();
        console.log(selectedDocument);
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [selectedDocument]);

    useEffect(() => {
        FetchList();
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [business]);

    return (
        <Stack w={'full'} justify={"center"} align={"center"} spacing={6}>
            <Stack
                direction={{ base: 'column', md: 'column', lg: 'column', xl: 'column', "2xl": "row" }}
                justify={'space-between'}
                boxShadow={'base'}
                rounded={'xl'}
                w={'98%'}
                p={4}
                spacing={{ base: 6, md: 6, lg: 6, xl: 6, "2xl": 0 }}
            >
                <Stack
                    align={'flex-start'}
                    justify={'start'}
                    spacing={4}
                >
                    <Stack direction={'row'}>
                        <Icon
                            as={RiDeleteBin5Fill}
                            fontSize={'5xl'}
                            color={'primary.400'}
                        />
                        <Stack spacing={1}>
                            <Heading fontSize={'xl'}>Recycle Bin</Heading>
                            <Text fontSize={'sm'}>All Recycle Bin Documents are automatically deleted after 90 Days</Text>
                        </Stack>
                    </Stack>
                </Stack>
                <Stack align={{ lg: 'flex-start', "2xl": 'flex-end' }} direction={'row'} spacing={4}>
                </Stack>
            </Stack>

            <Stack boxShadow={'base'} rounded={'xl'} w={'98%'} p={4} spacing={4}>
                <Stack direction={{ base: 'column', md: 'row' }} spacing={3} align={{ md: 'center' }}>
                    <InputGroup size={'sm'} maxW={{ md: '280px' }}>
                        <InputLeftElement pointerEvents={'none'} children={<FaSearch color={'gray'} />} />
                        <Input placeholder={'Search deleted documents…'} value={search} onChange={(e) => setSearch(e.target.value)} />
                    </InputGroup>
                    <Select size={'sm'} maxW={{ md: '220px' }} placeholder={'All document types'} value={docTypeFilter} onChange={(e) => setDocTypeFilter(e.target.value)}>
                        {docTypes.map((t) => <option key={t} value={t}>{t}</option>)}
                    </Select>
                </Stack>
                <SimpleGrid
                    templateColumns={{ base: '1fr', md: '1fr 4fr 2fr' }}
                    spacing={6}
                >
                    <Stack w={'250px'}>
                        {
                            loading ? <Text>Loading...</Text> : 
                            <TableContainer>
                            <Table size='sm'>
                                <Thead h={'50px'} bg={'primary.400'} alignItems={'flex-start'}>
                                    <Tr>
                                        <Th isNumeric color={'white'}>
                                            ID
                                        </Th>
                                        <Th color={'white'}>
                                            Document Type
                                        </Th>
                                    </Tr>
                                </Thead>
                                <Tbody>
                                    {
                                        filteredDocs.map(item => (
                                            <Tr key={item.id}>
                                                <Td display={'flex'} justifyContent={'space-between'} >
                                                    <Checkbox id={item.id} colorScheme="primary" onChange={handleSelectedDocument} />
                                                    {item.id}
                                                </Td>
                                                <Td>
                                                    {item.documentType}
                                                </Td>
                                            </Tr>
                                        ))
                                    }
                                </Tbody>
                            </Table>
                        </TableContainer>
                        }
                        
                    </Stack>
                    <Stack>
                    <Stack w={'full'} h={'full'} align={'center'} justify={'center'} textAlign={'center'} bg={'gray.500'}>
                            {
                                selectedDocumentPDF.length > 0 ?
                                    <DocViewer
                                        ref={docViewerRef}
                                        pluginRenderers={DocViewerRenderers}
                                        documents={selectedDocumentPDF}
                                        config={{
                                            // header: {
                                            //     overrideComponent: DocumentHeader,
                                            // },
                                            csvDelimiter: ",",
                                            pdfZoom: {
                                                defaultZoom: 1.1,
                                                zoomJump: 0.2,
                                            },
                                            pdfVerticalScrollByDefault: true,
                                        }}
                                        style={{ overflowY: 'auto' }}
                                        theme={{
                                            primary: "#ffffffeb",
                                            secondary: "#ffffff",
                                            tertiary: "#05445d",
                                            textPrimary: "#000000",
                                            textSecondary: "#ffffff",
                                            textTertiary: "#ffffff",
                                        }}
                                    />
                                    : <>
                                        <Heading color={'white'}>Document Viewer</Heading>
                                        <Text color={'whiteAlpha.900'} padding={'0 10px'}>No Documents Selected! Select Any Document To Preview Here.</Text>
                                    </>
                            }
                        </Stack>
                    </Stack>
                    <Stack w={'full'} spacing={6} divider={<StackDivider />}>
                        <Stack direction={'row'} justify={'space-between'} align={'center'}>
                            <Stack direction={'row'}>
                                <IconButton
                                    aria-label="Previous"
                                    icon={<FaArrowLeft />}
                                    rounded={'full'}
                                    size={'sm'}
                                    colorScheme={'orange'}
                                />
                                <IconButton
                                    aria-label="Previous"
                                    icon={<FaArrowRight />}
                                    rounded={'full'}
                                    size={'sm'}
                                    colorScheme={'orange'}
                                />
                            </Stack>
                            <Stack direction={'row'}>
                                <Button
                                    leftIcon={<FaUndo />}
                                    color={'white'}
                                    bg={'primary.400'}
                                    _hover={{ bg: 'primary.400' }}
                                    _active={{ bg: 'primary.400' }}
                                    size={'sm'}
                                    onClick={handleRestore}
                                >
                                    Restore
                                </Button>
                                <Button
                                    leftIcon={<FaTrash />}
                                    color={'white'}
                                    bg={'red.400'}
                                    _hover={{ bg: 'red.500' }}
                                    _active={{ bg: 'red.600' }}
                                    size={'sm'}
                                    onClick={onDeleteOpen}
                                >
                                    Delete
                                </Button>
                            </Stack>
                        </Stack>
                        <Stack spacing={4}>
                            <Heading fontSize={'lg'}>Document Type</Heading>
                            <Text>{selectedDocument[0]?.documentType}</Text>
                        </Stack>
                        <Stack spacing={4}>
                            <Heading fontSize={'lg'}>Document Properties</Heading>
                            <Text>Document Id: <strong>{selectedDocument[0]?.id}</strong></Text>
                            <Text>Status: <strong>{selectedDocument[0]?.status}</strong></Text>
                            <Text>Uploaded By: <strong>{selectedDocument[0]?.user?.email}</strong></Text>
                            <Text>Upload Date: <strong>{selectedDocument[0]?.createdAt}</strong></Text>
                            <Text>Source: <strong>{selectedDocument[0]?.source}</strong></Text>
                        </Stack>
                    </Stack>
                </SimpleGrid>
            </Stack>
            <Modal isOpen={isDeleteOpen} onClose={onDeleteClose} isCentered>
                <ModalOverlay bg={'blackAlpha.400'} backdropFilter={'blur(6px)'} />
                <ModalContent>
                    <ModalHeader>Reason for deletion</ModalHeader>
                    <ModalBody>
                        <Text fontSize={'sm'} color={'gray.600'} mb={2}>
                            Document ID(s): {selectedDocuments.join(', ') || '—'}
                        </Text>
                        <Textarea
                            placeholder={'Why are you permanently deleting this document?'}
                            value={deleteReason}
                            onChange={(e) => setDeleteReason(e.target.value)}
                            rows={4}
                        />
                    </ModalBody>
                    <ModalFooter>
                        <Button variant={'ghost'} onClick={onDeleteClose} mr={2}>Cancel</Button>
                        <Button colorScheme={'red'} onClick={confirmDelete} isDisabled={!deleteReason.trim()}>
                            Delete permanently
                        </Button>
                    </ModalFooter>
                </ModalContent>
            </Modal>
        </Stack >
    )
}